import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { ChaveNotificacao, ContadoresNotificacao } from "../interfaces/INotificacoes";

interface INotificacoesContext {
    /** Contadores por chave — o menu lê daqui para desenhar os badges. */
    contadores: ContadoresNotificacao;
    /** Publica o total de uma chave. Chamado por quem é dono da informação. */
    definirContador: (chave: ChaveNotificacao, total: number) => void;
    /** Soma dos contadores de um conjunto de chaves (badge agregado do módulo). */
    somarContadores: (chaves: (ChaveNotificacao | undefined)[]) => number;
    /** Soma de tudo — usado onde o menu não está visível (ex.: botão do menu no mobile). */
    totalNotificacoes: number;
}

const NotificacoesContext = createContext<INotificacoesContext>({
    contadores: {},
    definirContador: () => { },
    somarContadores: () => 0,
    totalNotificacoes: 0,
});

export const useNotificacoesContext = () => useContext(NotificacoesContext);

/**
 * Ponto único dos contadores de notificação do sistema.
 *
 * O menu não conhece a origem dos números: cada feature publica o seu contador
 * e o menu apenas desenha. Assim uma notificação nova (pedidos pendentes,
 * alertas de estoque...) entra sem tocar no componente de navegação.
 */
export const NotificacoesProvider = ({ children }: { children: ReactNode }) => {
    const [contadores, setContadores] = useState<ContadoresNotificacao>({});

    const definirContador = useCallback((chave: ChaveNotificacao, total: number) => {
        const valor = Number.isFinite(total) ? Math.max(0, Math.trunc(total)) : 0;

        setContadores(atual => (atual[chave] === valor ? atual : { ...atual, [chave]: valor }));
    }, []);

    const somarContadores = useCallback(
        (chaves: (ChaveNotificacao | undefined)[]) =>
            chaves.reduce<number>((total, chave) => total + (chave ? contadores[chave] ?? 0 : 0), 0),
        [contadores]
    );

    const totalNotificacoes = useMemo(
        () => Object.values(contadores).reduce<number>((total, valor) => total + (valor ?? 0), 0),
        [contadores]
    );

    const valor = useMemo(
        () => ({ contadores, definirContador, somarContadores, totalNotificacoes }),
        [contadores, definirContador, somarContadores, totalNotificacoes]
    );

    return <NotificacoesContext.Provider value={valor}>{children}</NotificacoesContext.Provider>;
};
